import { useCallback, useRef, useState } from 'react';

import { TailwindWrapper } from '@utils/TailwindWrapper';
import { Text, Button, Textarea, TwFileUpload, Icon } from '@ui-component-library';

import { deleteAdAttachmentsNew } from '../../../../api/services/ad';
import { submitSellTimberGrotAd } from './helpers/api';
import {
  ACTION_OPTIONS,
  buildGrotSuggestedComment,
  grotCommentsForApi,
  grotHasAtLeastOneAttachment,
  grotStep3ValidationMessages,
  hasGrotBiobransleMarkerPlacement,
  labelGrotJaNej,
  SELL_TIMBER_GENERIC_ERROR
} from './helpers/sellTimberShared';
import {
  SELL_TIMBER_FIELD_INFO,
  SellTimberFieldInfo
} from './components/SellTimberFieldInfo.js';
import SellTimberMap from './components/SellTimberMap';
import { SummaryEditButton, SummaryRow } from './components/SellTimberSummary';
import { MAP_MODES } from './helpers/sellTimberMapStep2State';

const EMPTY_ATTACHMENTS = [];

function labelFor(options, value) {
  const o = options.find((x) => x.value === value);
  return o?.label ?? value ?? '-';
}

export default function SellTimberGrotStep4({ wizard, dispatch }) {
  const adCreateData = wizard.adCreateData;
  const geometry = wizard.geometry;
  const existingAdId = wizard.editingAdId;

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [attachmentError, setAttachmentError] = useState(null);

  /** Textarea calls onChange from an effect when `onChange` identity changes - ref + stable callback avoids SET_AD_CORE loops. */
  const adCreateDataRef = useRef(adCreateData);
  adCreateDataRef.current = adCreateData;

  const attachments = adCreateData?.attachments ?? EMPTY_ATTACHMENTS;
  const hasAttachment = grotHasAtLeastOneAttachment(attachments);

  const handleCommentsChange = useCallback((v) => {
    const data = adCreateDataRef.current;
    if (!data) return;
    dispatch({
      type: 'SET_AD_CORE',
      payload: { ...data, grotCommentsExtra: v }
    });
  }, [dispatch]);

  const handleGenerateSuggestedComment = useCallback(() => {
    const data = adCreateDataRef.current;
    if (!data) return;
    const text = buildGrotSuggestedComment({
      designation: data.selectedProperty?.designation,
      geometry,
      grot_volume: data.grot_volume,
      grot_heap_covered: data.grot_heap_covered,
      grot_collected: data.grot_collected
    });
    dispatch({
      type: 'SET_AD_CORE',
      payload: { ...data, grotCommentsExtra: text }
    });
  }, [dispatch, geometry]);

  const handleAttachmentsChange = useCallback((newFiles) => {
    const data = adCreateDataRef.current;
    if (!data) return;
    const prev = data.attachments ?? [];
    const nextIds = new Set(newFiles.map((f) => f.id));
    for (const p of prev) {
      if (p.existing && !nextIds.has(p.id)) {
        deleteAdAttachmentsNew(p.id).catch(() => {});
      }
    }
    const nextAttachments = newFiles.map((file) => {
      file.valid = true;
      return file;
    });
    dispatch({
      type: 'SET_AD_CORE',
      payload: { ...data, attachments: nextAttachments }
    });
    setAttachmentError(null);
    setSubmitError(null);
  }, [dispatch]);

  const handleSubmit = async () => {
    setSubmitError(null);
    const data = adCreateDataRef.current;
    if (!data) return;

    const step3Missing = grotStep3ValidationMessages({
      grot_volume: data.grot_volume,
      grot_heap_covered: data.grot_heap_covered,
      grot_collected: data.grot_collected
    });
    if (step3Missing.length) {
      setSubmitError(
        `Komplettera steg 3 innan du slutför: ${step3Missing.join(', ')}.`
      );
      return;
    }

    if (!grotHasAtLeastOneAttachment(data.attachments)) {
      setAttachmentError('Ladda upp minst en bild på biobränslet.');
      return;
    }
    setAttachmentError(null);
    setSubmitting(true);
    try {
      const result = await submitSellTimberGrotAd({
        adCreateData: { ...data, selectedAction: 'grot' },
        geometry,
        existingAdId
      });
      if (result.needsRedirect && result.redirectUrl) {
        window.location.assign(result.redirectUrl);
        return;
      }
      if (result.ok) {
        window.location.assign(`/auctions/${result.adId}/`);
        return;
      }
      setSubmitError(result.message ?? SELL_TIMBER_GENERIC_ERROR);
    } catch (e) {
      console.error(e);
      setSubmitError(SELL_TIMBER_GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const volDisplay =
    adCreateData?.grot_volume != null && String(adCreateData.grot_volume).trim()
      ? `${String(adCreateData.grot_volume).trim()} m³`
      : '-';

  const grotCommentsExtra = grotCommentsForApi(adCreateData) ?? '';

  return (
    <>
      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto">
          <div className="mx-auto flex max-w-full flex-col gap-8 p-6">
            <Text variant="text-xl/leading-normal/medium">Sammanfattning - Biobränsle</Text>
            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
              Kontrollera dina uppgifter. Klicka på redigera för att ändra ett steg.
            </Text>

            <section className="flex flex-col gap-3 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <Text variant="text-lg/leading-normal/medium">Steg 1 - Uppgifter</Text>
              <SummaryRow
                label="Fastighet"
                value={adCreateData?.selectedProperty?.designation ?? '-'}
                valueClassName="capitalize"
              />
              <SummaryRow
                label="Typ av åtgärd"
                value={labelFor(ACTION_OPTIONS, adCreateData?.selectedAction)}
              />
              <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
                <Text variant="text-sm/leading-normal/medium" className="min-w-[10rem] text-charcoal-700">
                  Annonsperiod
                </Text>
                <Text variant="text-sm/leading-normal/normal" className="text-charcoal-900">
                  Din annons ligger synlig i tre veckor från och med dagen Treebula publicerar den.
                </Text>
              </div>
              <SummaryEditButton
                step={1}
                onEditStep={(s) => dispatch({ type: 'SET_STEP', step: s })}
                disabled={submitting}
              />
            </section>

            <section className="flex flex-col gap-2 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <Text variant="text-lg/leading-normal/medium">Steg 2 - Plats på kartan</Text>
              <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                Markören visar var biobränslet finns på fastigheten.
              </Text>
              <SummaryEditButton
                step={2}
                onEditStep={(s) => dispatch({ type: 'SET_STEP', step: s })}
                disabled={submitting}
              />
            </section>
          </div>
        </div>
      </TailwindWrapper>

      {geometry ? (
        <div
          style={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 24px 24px'
          }}
        >
          <SellTimberMap
            selection={{
              mapMode: MAP_MODES.marker,
              sections: geometry.sections ?? [],
              borderCoords: geometry.borderCoords ?? null,
              fellingApplicationsGeojson: geometry.fellingApplicationsGeojson ?? null,
              selectedSectionLocalIds: [],
              selectedFellingApplicationIds: [],
              drawnPolygons: [],
              placedMarkers: geometry.placedMarkers ?? null,
              customAreaFromAd: null
            }}
            handlers={{}}
            options={{
              readOnly: true,
              onlySelectedOverlays: true,
              mapId: 'sell_timber_grot_summary_leaflet'
            }}
          />
        </div>
      ) : null}

      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto">
          <div className="mx-auto flex max-w-full flex-col gap-8 p-6 pt-0">
            <section className="flex flex-col gap-3 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <Text variant="text-lg/leading-normal/medium">Steg 3 - Biobränsle</Text>
              <SummaryRow label="Volym" value={volDisplay} />
              <SummaryRow
                label="Biobränslet täckt"
                value={labelGrotJaNej(adCreateData?.grot_heap_covered)}
              />
              <SummaryRow
                label="Biobränslet uppsamlat"
                value={labelGrotJaNej(adCreateData?.grot_collected)}
              />
              <SummaryEditButton
                step={3}
                onEditStep={(s) => dispatch({ type: 'SET_STEP', step: s })}
                disabled={submitting}
              />
            </section>

            <section className="flex flex-col gap-3 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Text variant="text-lg/leading-normal/medium" as="span">
                  Övrig information
                </Text>
                <SellTimberFieldInfo
                  title="Övrig information"
                  text={SELL_TIMBER_FIELD_INFO.ovrigInformation}
                  aria-label="Information om övrig information"
                />
              </div>
              <div className="flex flex-col gap-3">
                <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                  Fyll i den information du vill att köparna ska ta del av gällande biobränslet. Du kan
                  även generera en automatisk annonstext genom att klicka på knappen nedan.
                </Text>
                <Button
                  tw
                  type="button"
                  variant="secondary"
                  size="small"
                  className="inline-flex items-center gap-2 self-start"
                  disabled={
                    submitting ||
                    !hasGrotBiobransleMarkerPlacement(geometry) ||
                    grotStep3ValidationMessages({
                      grot_volume: adCreateData?.grot_volume,
                      grot_heap_covered: adCreateData?.grot_heap_covered,
                      grot_collected: adCreateData?.grot_collected
                    }).length > 0
                  }
                  onClick={handleGenerateSuggestedComment}
                  aria-label="Generera förslag på text från biobränsleuppgifter och kartmarkörer"
                >
                  Generera text
                  <Icon
                    tw
                    name="SparkleSimple"
                    size="small"
                    className="shrink-0 fill-charcoal-700"
                    aria-hidden
                  />
                </Button>
              </div>
              <Textarea
                tw
                name="grot_comments_extra"
                value={grotCommentsExtra}
                rows={4}
                onChange={handleCommentsChange}
                placeholder="Valfri text…"
                disabled={submitting}
              />
            </section>

            <section className="flex flex-col gap-3 rounded-lg border border-charcoal-200 bg-cloud-200/40 p-4">
              <Text variant="text-lg/leading-normal/medium" as="span">
                Bild på biobränslet
              </Text>
              <TwFileUpload
                tw
                name="grot_attachment_files"
                accept=".png,.jpg,.jpeg,.webp,.gif"
                label=""
                multiple
                buttonText="Välj bild"
                value={attachments}
                onChange={handleAttachmentsChange}
                idleText="Ladda upp en bild på biobränslet. Minst en bild krävs för att slutföra annonsen."
                errorText={SELL_TIMBER_GENERIC_ERROR}
                successText="Bilden är uppladdad."
                disabled={submitting}
              />
              {attachmentError ? (
                <Text variant="text-sm/leading-normal/normal" className="text-red-700" role="alert">
                  {attachmentError}
                </Text>
              ) : null}
            </section>

            {submitError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700" role="alert">
                {submitError}
              </Text>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <Button
                tw
                type="button"
                variant="tertiary"
                onClick={() => dispatch({ type: 'SET_STEP', step: 3 })}
                disabled={submitting}
              >
                Tillbaka
              </Button>
              <Button
                tw
                type="button"
                variant="primary"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Slutför…' : 'Slutför'}
              </Button>
            </div>
          </div>
        </div>
      </TailwindWrapper>
    </>
  );
}
